import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import PageHeader from '../../components/shell/PageHeader';
import IconButton from '../../components/ui/IconButton';
import StatusPill from '../../components/ui/StatusPill';
import EmptyState from '../../components/ui/EmptyState';
import { SkeletonBlock } from '../../components/ui/Skeleton';
import Modal from '../../components/overlays/Modal';
import { Field, TextInput, Textarea } from '../../components/ui/Field';
import { useOverlays } from '../../components/overlays/OverlayProvider';
import {
  deleteVideo,
  homeVideoList,
  updateVideoDetails,
  uploadVideo,
} from '../../actions/homeVideoAction';
import { formatDate } from '../../helpers/format';
import ImageThumbnail from '../../images/image_thumbnail.jpg';
import './growth.css';

const EMPTY = { title: '', description: '', video_link: '', start_date: '', end_date: '', file: null };

const SECTIONS = [
  { key: 'active_videos', label: 'Active', tone: 'live' },
  { key: 'queue_videos', label: 'Queued', tone: 'review' },
  { key: 'expire_videos', label: 'Expired', tone: 'neutral' },
];

export default function AppVideo() {
  const dispatch = useDispatch();
  const { confirm, toast } = useOverlays();
  const [groups, setGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await homeVideoList();
      setGroups(res?.data || {});
    } catch {
      setGroups({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setForm(EMPTY);
    setEditing('new');
  };

  const openEdit = (video) => {
    setForm({
      title: video.title || '',
      description: video.description || '',
      video_link: video.link || '',
      start_date: (video.start_date || '').slice(0, 16),
      end_date: (video.end_date || '').slice(0, 16),
      file: null,
    });
    setEditing(video);
  };

  const save = useCallback(async () => {
    setBusy(true);
    const data = new FormData();
    data.append('title', form.title);
    data.append('description', form.description || '');
    data.append(editing === 'new' ? 'video_link' : 'link', form.video_link);
    data.append('start_date', form.start_date);
    data.append('end_date', form.end_date);
    if (form.file) data.append('file', form.file);

    try {
      if (editing === 'new') await uploadVideo(dispatch, data);
      else await updateVideoDetails(dispatch, data, editing.id);
      toast(editing === 'new' ? 'Video added' : 'Video updated', { tone: 'success' });
      setEditing(null);
      load();
    } catch {
      toast('Could not save the video', { tone: 'error' });
    } finally {
      setBusy(false);
    }
  }, [dispatch, editing, form, toast, load]);

  const remove = useCallback(
    async (video) => {
      const ok = await confirm({
        title: `Delete “${video.title}”?`,
        body: 'The video is removed from the app immediately. This cannot be undone.',
        confirmLabel: 'Delete video',
        destructive: true,
      });
      if (!ok) return;
      try {
        await deleteVideo(dispatch, video.id);
        load();
      } catch {
        toast('Could not delete the video', { tone: 'error' });
      }
    },
    [confirm, dispatch, toast, load]
  );

  const total = SECTIONS.reduce((n, s) => n + (groups[s.key]?.length || 0), 0);

  return (
    <>
      <PageHeader
        section="Growth"
        title="App video"
        subtitle="Videos shown on the app home screen, grouped by whether they are live."
        primaryAction={{ label: 'New video', icon: 'bx-plus', onClick: openNew }}
      />

      <div className="sv-screen-body">
        {loading && (
          <div className="sv-media-grid">
            {Array.from({ length: 3 }).map((_, i) => (
              <div className="sv-media-card" key={i}>
                <SkeletonBlock width="100%" height={150} radius={0} />
                <div style={{ padding: 14 }}>
                  <SkeletonBlock width="70%" height={12} />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && total === 0 && (
          <div className="sv-card" style={{ marginTop: 14 }}>
            <EmptyState
              icon="bx-video"
              title="No videos yet"
              description="Add a video to show it on the app home screen."
              actionLabel="New video"
              actionIcon="bx-plus"
              onAction={openNew}
            />
          </div>
        )}

        {!loading &&
          SECTIONS.map((section) => {
            const videos = groups[section.key] || [];
            if (videos.length === 0) return null;
            return (
              <section className="sv-media-section" key={section.key}>
                <div className="sv-media-section__head">
                  <h2 className="sv-media-section__title">{section.label}</h2>
                  <span className="sv-media-section__count">{videos.length}</span>
                </div>
                <div className="sv-media-grid">
                  {videos.map((video) => (
                    <article className="sv-media-card" key={video.id}>
                      <img
                        className="sv-media-card__thumb"
                        src={video.thumbnail_url || ImageThumbnail}
                        alt=""
                        onError={(e) => {
                          e.currentTarget.src = ImageThumbnail;
                        }}
                      />
                      <div className="sv-media-card__body">
                        <div className="sv-media-card__title sv-truncate">{video.title}</div>
                        <div className="sv-media-card__meta">
                          {formatDate(video.start_date)} – {formatDate(video.end_date)}
                        </div>
                      </div>
                      <div className="sv-media-card__foot">
                        <StatusPill tone={section.tone}>{section.label}</StatusPill>
                        <span style={{ display: 'flex', gap: 4 }}>
                          <IconButton icon="bx-edit" size="sm" label="Edit" onClick={() => openEdit(video)} />
                          <IconButton icon="bx-trash" size="sm" label="Delete" onClick={() => remove(video)} />
                        </span>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            );
          })}
      </div>

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'New video' : 'Edit video'}
        size="form"
        confirmLabel={editing === 'new' ? 'Add video' : 'Save changes'}
        confirmLoading={busy}
        confirmDisabled={!form.title.trim() || !form.video_link.trim()}
        onConfirm={save}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 4 }}>
          <Field label="Title" required>
            <TextInput value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </Field>
          <Field label="Video link" required>
            <TextInput
              value={form.video_link}
              onChange={(e) => setForm({ ...form, video_link: e.target.value })}
              placeholder="https://youtube.com/watch?v=…"
            />
          </Field>
          <Field label="Description">
            <Textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Starts" required>
              <TextInput
                type="datetime-local"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              />
            </Field>
            <Field label="Ends" required>
              <TextInput
                type="datetime-local"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              />
            </Field>
          </div>
          <Field label="Thumbnail" hint="Leave empty to keep the current image">
            <input type="file" accept="image/*" onChange={(e) => setForm({ ...form, file: e.target.files[0] })} />
          </Field>
        </div>
      </Modal>
    </>
  );
}
